import React, { useState } from 'react';
import type { Event, Artist } from '../../types';
import { useLang } from '../../i18n/context';
import { useAuth } from '../../contexts/AuthContext';
import { EditEventModal } from './EditEventModal';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Only show venue address if it's a real street address, not geocoding noise */
function usableAddress(address?: string, venueName?: string): boolean {
  if (!address) return false;
  // Geocoding verbose output always contains multilingual separators
  if (address.includes(' / ')) return false;
  // Too long → likely Nominatim hierarchy, not a street address
  if (address.length > 80) return false;
  // Venue name already IS an address (contains digit + comma) → redundant
  if (venueName && /\d/.test(venueName) && venueName.includes(',')) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Parse time + lineup from description / linked artists
// ---------------------------------------------------------------------------

function parseEventTime(description?: string): { start: string; end?: string } | null {
  if (!description) return null;

  const fmt = (h: string, min?: string) =>
    `${h.padStart(2, '0')}:${(min || '00').padStart(2, '0')}`;

  // Pattern 1: "Horaire: 21:00 - 05:00" (OCR stub)
  let m = description.match(/horaire\s*:?\s*(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})/i);
  if (m) return { start: m[1], end: m[2] };

  // Pattern 2: adjacent range "21H—05H", "21h > 05h", "21:00 - 05:00"
  m = description.match(/\b(\d{1,2})[hH:u](\d{0,2})\s*[-–—>]\s*(\d{1,2})[hH:u](\d{0,2})\b/);
  if (m) {
    const start = fmt(m[1], m[2]), end = fmt(m[3], m[4]);
    if (start !== end) return { start, end };
  }

  // Helper: scan separately for an end time preceded by "until/till/tot/bis/ends at/goes until"
  const findEnd = (): string | undefined => {
    const em = description!.match(
      /(?:goes?\s+until|until|till|tot\s+|bis\s+|ends?\s+at|jusqu'[aà])\s*(\d{1,2})[hHu:.](\d{0,2})/i
    );
    return em ? fmt(em[1], em[2]) : undefined;
  };

  // Pattern 3: explicit start keyword — "starts at 20:00", "from 21h", "van 20u", "doors: 20:00"
  const startM = description.match(
    /(?:starts?\s+at|dance\s+starts?\s+at|from|de\s+|van\s+|vanaf\s+|dès\s+|doors?\s*(?:at|open|:)?\s*|open(?:ing)?\s+at)\s*(\d{1,2})[hHu:.](\d{0,2})/i
  );
  if (startM) {
    const start = fmt(startM[1], startM[2]);
    const end = findEnd();
    return end && end !== start ? { start, end } : { start };
  }

  // Pattern 4: single time fallback — first HH:MM / HHuMM found
  m = description.match(/\b(\d{1,2})[u:](\d{2})\b/);
  if (m) {
    const start = fmt(m[1], m[2]);
    const end = findEnd();
    return end && end !== start ? { start, end } : { start };
  }

  return null;
}

interface ScheduleEntry { time?: string; name: string; }

function parseSchedule(description?: string, artists?: Artist[]): ScheduleEntry[] {
  // Priority 1: DB-linked artists — curated, verified, most reliable
  // These come from matchKnownArtists + enrichment pipeline
  const dbArtists = (artists && artists.length > 0)
    ? artists.map(a => ({ name: a.name }))
    : [];

  // Priority 2: Timed schedule from description — adds time info
  // "22:00 - Kingstep mts Jah Vibes" → merge times with DB artists if possible
  if (description) {
    const matches = [...description.matchAll(
      /(\d{1,2})[u:](\d{2})\s*[-–]\s*(?!doors?\b|porte\b|open\b)([^\n\r]{3,70})/gi
    )];
    if (matches.length >= 2) {
      const timedEntries = matches
        .map(m => ({
          time: `${m[1].padStart(2, '0')}:${m[2]}`,
          name: m[3].trim().replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim(),
        }))
        .filter(e => e.name.length > 2)
        .filter(e => !/^\d{1,2}[h:u]\d{0,2}$/.test(e.name))
        .filter(e => /[a-zA-ZÀ-ÿ]/.test(e.name));

      if (timedEntries.length >= 2) {
        // If we have DB artists, merge: use timed schedule as primary (has time info)
        // and add any DB artists not already in the timed list
        if (dbArtists.length > 0) {
          const timedNorm = new Set(timedEntries.map(e => e.name.toLowerCase().replace(/[^a-z0-9]/g, '')));
          const extras = dbArtists.filter(a => {
            const norm = a.name.toLowerCase().replace(/[^a-z0-9]/g, '');
            return !timedNorm.has(norm);
          });
          return [...timedEntries, ...extras];
        }
        return timedEntries;
      }
    }
  }

  // If we have DB artists, use them directly
  if (dbArtists.length > 0) return dbArtists;

  // Priority 3: "LINEUP:" section from description — fallback only
  if (description) {
    const lineupMatch = description.match(/(?:line-?up|artists?|featuring|lineup)\s*[:：]\s*([^\n]{5,})/i);
    if (lineupMatch) {
      const names = lineupMatch[1]
        .split(/[,;/&+]|\bvs\.?\b|\bmeets?\b|\bft\.?\b/i)
        .map(n => n.trim().replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '').trim())
        .filter(n => n.length > 2 && n.length < 50)
        .filter(n => /[a-zA-ZÀ-ÿ]/.test(n))
        .filter(n => !/^\d/.test(n));
      if (names.length > 0) return names.map(name => ({ name }));
    }
  }

  return [];
}

interface EventCardProps {
  event: Event;
  onUpdate?: () => void;
  showActions?: boolean;
  forceExpanded?: boolean; // when true, overrides internal expand state — used by validation tab
}

function getTicketLabel(event: Event, t: (key: string, params?: Record<string, string | number>) => string): { label: string; style: React.CSSProperties } {
  const isFree = event.is_free || event.price_info?.toLowerCase().includes('gratuit') || event.price_info?.toLowerCase().includes('free');

  if (isFree) {
    return { label: '✓ FREE', style: {
      color: '#4caf50', borderColor: '#4caf50',
      background: 'rgba(76,175,80,0.12)',
      boxShadow: '0 0 8px rgba(76,175,80,0.3), inset 0 0 4px rgba(76,175,80,0.1)',
      textShadow: '0 0 6px rgba(76,175,80,0.5)',
      fontWeight: 700, letterSpacing: '1px',
    } };
  }
  if (event.price_info && event.price_info !== '0') {
    return { label: `🎟 ${event.price_info}`, style: {} };
  }
  if (event.price && event.price > 0) {
    return { label: `🎟 ${event.price}€`, style: {} };
  }
  // Has a real ticket URL or detected ticket keywords but no price
  if (event.ticket_url) {
    return { label: '🎟 Get tickets', style: { opacity: 0.7 } };
  }
  if (event.has_tickets) {
    return { label: `🎟 ${t('events.price_unknown')}`, style: { opacity: 0.7 } };
  }
  return { label: '', style: {} };
}

export function EventCard({ event, onUpdate, showActions = true, forceExpanded }: EventCardProps) {
  const [internalExpanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const expanded = forceExpanded !== undefined ? forceExpanded : internalExpanded;
  const { t } = useLang();
  const { isAdmin } = useAuth();

  const formatDate = (d: string) => {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d; // Safety: show raw string if date is invalid
    const days = t('date.days').split(',').map(s => s.slice(0, 3).toUpperCase());
    // months_short = explicit locale-proper abbreviations (naive slice(0,3) made juin↔juillet both "JUI")
    const months = t('date.months_short').split(',').map(s => s.toUpperCase());
    return `${days[date.getDay()]}. ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const hasFlyer = event.flyer_url || event.image_url;
  // Prefer the LOCAL copy over the remote URL: Instagram/Facebook CDN URLs
  // expire after hours-days, while our /api/images/ files are persistent.
  // If the local file is missing (rare bug), onError falls back to the remote.
  const primaryFlyer = event.image_url || event.flyer_url;
  const fallbackFlyer = event.image_url && event.flyer_url && event.image_url !== event.flyer_url
    ? event.flyer_url
    : undefined;

  const ticketUrl = event.ticket_url || buildTicketUrl(event);
  const detailUrl = event.source_url || '#';
  const isFree = event.is_free || event.price_info?.toLowerCase().includes('gratuit') || event.price_info?.toLowerCase().includes('free');
  const ticket = getTicketLabel(event, t);

  return (
    <div className="event-card">
      <div className="event-card-top-bar" />
      <div className="event-card-body">
        {/* Flyer image */}
        {hasFlyer && (
          <div style={{
            marginBottom: '10px',
            borderRadius: '6px',
            overflow: 'hidden',
            // Collapsed: a 160px teaser strip. Expanded: show the WHOLE flyer but
            // cap it to the viewport so tall images stay readable (the admin
            // validation view was rendering full-size, unusable flyers).
            maxHeight: expanded ? '55vh' : '160px',
            display: 'flex', justifyContent: 'center',
            background: expanded ? 'rgba(0,0,0,0.25)' : undefined,
          }}>
            <img
              src={primaryFlyer}
              alt={event.title}
              style={{
                width: expanded ? 'auto' : '100%',
                maxWidth: '100%',
                maxHeight: expanded ? '55vh' : '160px',
                display: 'block',
                objectFit: expanded ? 'contain' : 'cover',
                borderRadius: '6px',
              }}
              loading="lazy"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                // First failure: try the remote fallback if we have one and haven't tried it yet.
                if (fallbackFlyer && img.src !== fallbackFlyer) {
                  img.src = fallbackFlyer;
                  return;
                }
                img.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="event-date">{formatDate(event.date)}</div>
        <div className="event-title">{event.title}</div>

        {event.venue_name ? (
          <>
            <div className="event-venue">
              &#128205; {event.venue_name}{(() => {
                const city = event.city || event.venue_city;
                // Don't append city if it's already in the venue name
                if (!city || event.venue_name!.toLowerCase().includes(city.toLowerCase().replace(/ \(.*\)$/, ''))) return '';
                return ` - ${city}`;
              })()}
            </div>
            {usableAddress(event.venue_address, event.venue_name) && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '-6px', marginBottom: '10px', paddingLeft: '22px' }}>
                {event.venue_address}
              </div>
            )}
          </>
        ) : event.venue_address ? (
          <div className="event-venue">
            &#128205; {event.venue_address}{(event.city || event.venue_city) ? ` - ${event.city || event.venue_city}` : ''}
          </div>
        ) : event.city ? (
          <div className="event-venue">
            &#128205; {event.city}
          </div>
        ) : (
          <div className="event-venue tbc">&#128205; {t('events.venue_tbc')}</div>
        )}

        {event.genre_tags?.length > 0 && (
          <div className="genre-tags">
            {event.genre_tags.map(g => (
              <span key={g} className={`genre-tag ${g.replace(/\s+/g, '-')}`}>{g}</span>
            ))}
          </div>
        )}

        {/* ── Timing + Lineup ── */}
        {(() => {
          // Prefer DB-stored OCR times; fall back to description parsing
          const dbTime = event.start_time
            ? { start: event.start_time, end: event.end_time }
            : null;
          const timeInfo = dbTime ?? parseEventTime(event.description);
          const schedule = parseSchedule(event.description, event.artists);
          const visibleItems = expanded ? schedule : schedule.slice(0, 3);
          const hiddenCount = schedule.length - 3;

          return (
            <>
              {/* Timing row */}
              {timeInfo && (
                <div className="event-timing">
                  <span className="event-timing-icon">◷</span>
                  <span className="event-timing-value">{timeInfo.start}</span>
                  <span className="event-timing-sep" />
                  <span className="event-timing-value event-timing-end">
                    {timeInfo.end ?? t('events.late')}
                  </span>
                </div>
              )}

              {/* Lineup */}
              {schedule.length > 0 && (
                <div className="event-lineup">
                  <div className="event-lineup-header">{t('events.lineup').toUpperCase()}</div>
                  {visibleItems.map((entry, i) => (
                    <div key={i} className="event-lineup-item">
                      <span className="event-lineup-name">{entry.name}</span>
                      {entry.time && (
                        <span className="event-lineup-time">{entry.time}</span>
                      )}
                    </div>
                  ))}
                  {!expanded && hiddenCount > 0 && (
                    <div className="event-lineup-more">{t('events.more', { n: hiddenCount })}</div>
                  )}
                </div>
              )}
            </>
          );
        })()}

        {/* Expandable description */}
        {expanded && event.description && (
          <div style={{
            fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px',
            padding: '8px', background: 'rgba(255,255,255,0.03)', borderRadius: '4px',
            lineHeight: 1.5, whiteSpace: 'pre-wrap',
          }}>
            {event.description}
          </div>
        )}
      </div>

      {showActions && (
        <div className="event-card-actions">
          {/* Ticket / Free / Price button */}
          {isFree ? (
            // Free event — always shows "FREE" with glow
            <span className="btn btn-sm" style={{
              color: '#4caf50', borderColor: '#4caf50',
              background: 'rgba(76,175,80,0.12)',
              boxShadow: '0 0 8px rgba(76,175,80,0.3), inset 0 0 4px rgba(76,175,80,0.1)',
              textShadow: '0 0 6px rgba(76,175,80,0.5)',
              fontWeight: 700, letterSpacing: '1px',
            }}>
              ✓ FREE
            </span>
          ) : ticketUrl && ticket.label ? (
            // Has ticket link -> clickable with price (or "Prix inconnu")
            <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary" style={ticket.style}>
              {ticket.label}
            </a>
          ) : ticket.label ? (
            // Has price but no ticket link
            <span className="btn btn-sm" style={ticket.style}>
              {ticket.label}
            </span>
          ) : null}

          {/* Details / Source link */}
          {detailUrl && detailUrl !== '#' && (
            <a href={detailUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm">
              {getSourceIcon(detailUrl)} {t('events.details')} &rsaquo;
            </a>
          )}

          {isAdmin && (
            <button
              className="btn btn-sm"
              onClick={() => setEditing(true)}
              style={{ marginLeft: 'auto', fontSize: '11px', borderColor: 'var(--gold)', color: 'var(--gold)' }}
              title={t('edit_event.title')}
            >
              ✎ {t('edit_event.edit')}
            </button>
          )}
          {forceExpanded === undefined && (
            <button
              className="btn btn-sm"
              onClick={() => setExpanded(!internalExpanded)}
              style={{ marginLeft: isAdmin ? '0' : 'auto', opacity: 0.6, fontSize: '11px' }}
            >
              {expanded ? '▲' : '▼'}
            </button>
          )}
        </div>
      )}

      {editing && (
        <EditEventModal
          event={event}
          onClose={() => setEditing(false)}
          onUpdated={() => { setEditing(false); onUpdate?.(); }}
        />
      )}
    </div>
  );
}

/** Try to build a ticket URL from the source URL or event data */
function buildTicketUrl(event: Event): string | undefined {
  if (event.source_url) {
    const src = event.source_url.toLowerCase();
    // Direct ticketing platforms only — NOT Facebook events (those are just event pages, not ticket platforms)
    if (src.includes('shotgun.live') || src.includes('eventbrite') ||
        src.includes('ticketswap') || src.includes('dice.fm') ||
        src.includes('tickets.') || src.includes('abconcerts') ||
        src.includes('djguide') || src.includes('ra.co') ||
        src.includes('billetweb') || src.includes('weezevent') ||
        src.includes('resident-advisor') || src.includes('ticketmaster') ||
        src.includes('fnacspectacles') || src.includes('seetickets') ||
        src.includes('ordolio.com') || src.includes('paylogic')) {
      return event.source_url;
    }
  }
  return undefined;
}

/** Get a small icon for the source type */
function getSourceIcon(url: string): string {
  if (url.includes('instagram.com')) return '📷';
  if (url.includes('facebook.com')) return '👥';
  if (url.includes('beldub.be')) return '🔊';
  if (url.includes('reggae.be')) return '🎵';
  if (url.includes('culturedub')) return '🎵';
  return '🔗';
}
